import { type Scope, createContextScope } from '@radix-ui/react-context'
import { SelectActions } from './components/select-actions'
import { SelectField } from './components/select-field'
import { SelectFieldContainer } from './components/select-field-container'
import { SelectHelperText } from './components/select-helper-text'
import { SelectIcon } from './components/select-icon'
import { SelectLabel } from './components/select-label'
import { useSelect } from './hook/use-select'
import type {
  SelectContext,
  SelectProps,
  SelectScopedProps,
} from './select.types'

const DISPLAY_NAME = 'Select'

const SelectRoot = (props: SelectScopedProps<SelectProps>) => {
  const {
    size = 'md',
    hasError = false,
    disabled = false,
    __scopeInputSelect,
    options,
    children,
    placeholder,
  } = props

  const { selectedOption } = useSelect()

  return (
    <SelectProvider
      {...props}
      size={size}
      scope={__scopeInputSelect}
      hasError={hasError}
      disabled={disabled}
      options={options}
      value={selectedOption?.value}
      placeholder={placeholder}
    >
      <div className="size-${size} min-w-[5rem] flex-1 flex-shrink-0 flex-col items-start gap-halfpulse rounded-none border-none bg-transparent p-none font-rdmodern text-align-center opacity-opaque">
        {children}
      </div>
    </SelectProvider>
  )
}

SelectRoot.displayName = DISPLAY_NAME

/*
----------------------------------------------------------------
Scope Definition
----------------------------------------------------------------
*/

const [createInputSelectContext] = createContextScope(DISPLAY_NAME)

export const [SelectProvider, useSelectContext]: readonly [
  ProviderType<SelectContext>,
  (consumerName: string, scope: Scope) => SelectContext,
] = createInputSelectContext<SelectContext>(DISPLAY_NAME)

/*
----------------------------------------------------------------
Composition Export
----------------------------------------------------------------
*/

export const Select = {
  Root: SelectRoot,
  Label: SelectLabel,
  FieldContainer: SelectFieldContainer,
  Icon: SelectIcon,
  Field: SelectField,
  Actions: SelectActions,
  HelperText: SelectHelperText,
}
